import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import Modal from "../../../components/common/modal/modal";
import ContactDetails from "../../../components/newSmartDevicePolicy/smartDeviceFlowSteps/contactDetails/contactDetails";
import PaymentSummary from "../../../components/newPersonalAccidentPolicy/personalAccidentFlowSteps/pay/paymentSummary";
import DeviceSelection from "../../../components/newSmartDevicePolicy/smartDeviceFlowSteps/deviceSelection/deviceSelection";
import PurchaseDetails from "../../../components/newSmartDevicePolicy/smartDeviceFlowSteps/purchaseDetails/purchaseDetails";
import SmartDeviceStartPage from "../../../components/newSmartDevicePolicy/smartDeviceFlowSteps/startPage/startPage";
import useSmartDeviceFlow from "../../../hooks/useSmartDeviceFlow";
import { selectLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import { clearAllQuotes } from "../../../redux/features/quoteSlice";
import { resetSmartDeviceFlow } from "../../../redux/features/smartDeviceFlowSlice";
import {
  clearNewSmartDeviceFormData,
  selectSmartDeviceForm,
} from "../../../redux/features/smartDeviceFormSlice";
import { clearSmartDeviceFiles } from "../../../utils/smartDeviceFilesStorage";
import { smartDeviceStepperData } from "./data";

const NewSmartDevicePolicy = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(true);
  const { currentStep, currentSubstep } = useSmartDeviceFlow();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const formData = useSelector(selectSmartDeviceForm);

  // Determine if user is guest (not logged in)
  const isGuest = !loginTokenInfo;

  // Clear payment redirect flag on component mount (user returned from payment or started fresh)
  useEffect(() => {
    const wasRedirectingToPayment = sessionStorage.getItem("isRedirectingToPayment");
    if (wasRedirectingToPayment === "true") {
      console.log("🔓 Clearing payment redirect flag - user returned or component mounted");
      sessionStorage.removeItem("isRedirectingToPayment");
    }
  }, []);

  // Clear form data when component unmounts or when user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(resetSmartDeviceFlow());
      dispatch(clearNewSmartDeviceFormData());
      dispatch(clearAllQuotes());
      clearSmartDeviceFiles();
    };

    const handlePopState = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(resetSmartDeviceFlow());
      dispatch(clearNewSmartDeviceFormData());
      dispatch(clearAllQuotes());
      clearSmartDeviceFiles();
    };

    // Add event listeners for browser navigation
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Cleanup function - runs when component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // CRITICAL: Don't clear flow state if we're redirecting to payment
      // This prevents the brief flash of the start screen before redirect
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on unmount - redirecting to payment");
        return;
      }

      // Clear form data and stored files when component unmounts
      dispatch(resetSmartDeviceFlow());
      dispatch(clearNewSmartDeviceFormData());
      dispatch(clearAllQuotes());
      clearSmartDeviceFiles();
    };
  }, [dispatch]);

  // Clear form data when user navigates away from the smart device policy route
  useEffect(() => {
    const currentPath = window.location.pathname;

    return () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on route change - redirecting to payment");
        return;
      }

      // Only clear if we're leaving the smart device policy routes
      if (!currentPath.includes("/smart-device")) {
        dispatch(resetSmartDeviceFlow());
        dispatch(clearNewSmartDeviceFormData());
        dispatch(clearAllQuotes());
      }
    };
  }, [dispatch]);

  const PageRenderer = () => {
    // Always show StartPage if currentStep is undefined or at the start
    if (!currentStep?.name || currentStep?.name === "START") {
      return <SmartDeviceStartPage onClose={handleCloseModal} />;
    }

    switch (currentStep?.name) {
      case "DEVICE":
        return <DeviceSelection onClose={handleCloseModal} />;
      case "PURCHASE_DETAILS":
        return <PurchaseDetails onClose={handleCloseModal} />;
      case "INFORMATION":
        return <ContactDetails />;
      case "REVIEW_PAY":
        // Handle substeps for review & pay
        return <PaymentSummary />;
      default:
        return <SmartDeviceStartPage onClose={handleCloseModal} />; // Fallback to start page
    }
  };

  const activeStepData = useMemo(() => {
    let activeStep = smartDeviceStepperData.find(
      (each) => each.key === currentStep.name
    );
    return activeStep;
  }, [currentStep]);

  const handleCloseModal = () => {
    // Reset smart device flow, form data, and quote data when modal is closed
    dispatch(resetSmartDeviceFlow());
    dispatch(clearNewSmartDeviceFormData());
    dispatch(clearAllQuotes());
    // Clear temporarily stored files (IMEI screenshot, CPR front/back)
    clearSmartDeviceFiles();
    setShowModal(false);
    navigate("/new-policy");
  };

  // Don't hide the header for any step
  const shouldHideHeader = false;

  return (
    <Modal show={showModal}>
      {!shouldHideHeader && (
        <Header
          isPopup
          hideMobileClose
          onCloseModal={handleCloseModal}
          showStepper={true}
          steps={smartDeviceStepperData}
          activeStep={activeStepData}
          isWideStepper={true}
        />
      )}
      <div id="sd-back-portal" />
      <div
        className={`mainSmartDeviceDetailsModal relative ${
          shouldHideHeader ? "pt-0" : "pt-5"
        } pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll max767:pt-0`}
      >
        <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={currentStep?.name || "START"}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {PageRenderer()}
            </motion.div>
          </AnimatePresence>
        {/* Stable mount point for the bottom sticky footer button, kept
            outside the animated/transformed wrapper to prevent flicker. */}
        <div id="sd-footer-portal" />
      </div>
    </Modal>
  );
};

export default NewSmartDevicePolicy;
